import type { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

/** Forward-progress statuses that clear the execution gate. */
const GATE_CLEARING_STATUSES: ReadonlySet<CohortDefinitionStatus> = new Set([
  'PROCESSING' as CohortDefinitionStatus,
  'PROCESSED' as CohortDefinitionStatus,
  'ERROR' as CohortDefinitionStatus,
  'FINAL' as CohortDefinitionStatus,
]);

/**
 * Terminal statuses among `GATE_CLEARING_STATUSES` — a run can only
 * legitimately reach one of these by first passing through PROCESSING.
 */
const TERMINAL_STATUSES: ReadonlySet<CohortDefinitionStatus> = new Set([
  'PROCESSED' as CohortDefinitionStatus,
  'ERROR' as CohortDefinitionStatus,
  'FINAL' as CohortDefinitionStatus,
]);

/**
 * Decides whether an incoming server status should be accepted or rejected.
 * Returns `{ accept: true, clearGate }` or `{ accept: false }`.
 *
 * `hasServerConfirmedProcessing` comes from an actual server response -
 * setProfileFromServer, setProfileStatusFromServer, or a socket event
 *
 * TODO: cleanup with skip_process_confirmation_modal ff
 */
export const applyStatusGate = (
  isExecutionPending: boolean,
  hasServerConfirmedProcessing: boolean,
  incomingStatus: CohortDefinitionStatus,
  useStrictProcessingGate: boolean
): { accept: boolean; clearGate: boolean } => {
  if (!isExecutionPending) return { accept: true, clearGate: false };

  // A terminal status (PROCESSED/ERROR/FINAL) arriving before the server has
  // confirmed PROCESSING for this run is stale — this can happen on
  // re-process where a response in flight before the click or a poll
  // finishing before the backend's commit reports the previous run's
  // outcome (e.g. the cohort was already PROCESSED before this click). Reject
  // it; only PROCESSING itself or a terminal status once PROCESSING has
  // been confirmed clears the gate.
  if (
    useStrictProcessingGate &&
    TERMINAL_STATUSES.has(incomingStatus) &&
    !hasServerConfirmedProcessing
  ) {
    return { accept: false, clearGate: false };
  }

  if (GATE_CLEARING_STATUSES.has(incomingStatus)) {
    return { accept: true, clearGate: true };
  }

  // DRAFT / UNSAVED during execution → stale, reject
  return { accept: false, clearGate: false };
};
